import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { filterByEnabledSince } from '../../services/notificationFilter';

interface AccessRequestResult {
  id: string;
  trackId: string;
  trackName: string;
  sourceOrgId: string;
  sourceOrgName?: string;
  decision: 'approved' | 'denied';
  denyReason?: string;
  decidedAt: number;
  decidedByName?: string;
  dismissed: boolean;
}

interface AccessRequestResultsModalProps {
  firebase: any;
}

const AccessRequestResultsModal: React.FC<AccessRequestResultsModalProps> = ({ firebase }) => {
  const [results, setResults] = useState<AccessRequestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();
  const isOpen = useNotificationsStore(state => state.showAccessRequestResultsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowAccessRequestResultsModal);
  const setAccessRequestResultsCount = useNotificationsStore(state => state.setAccessRequestResultsCount);

  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  useEffect(() => {
    if (!currentOrgId || !firebase?.subscribeAccessRequestResultsForOrg) {
      setResults([]);
      setAccessRequestResultsCount(0);
      return;
    }
    setLoading(true);
    const unsubscribe = firebase.subscribeAccessRequestResultsForOrg(
      currentOrgId,
      (fetched: AccessRequestResult[]) => {
        const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
        const filtered = filterByEnabledSince('accessRequestResults', fetched, enabledSince, (n) => n.decidedAt);
        setResults(filtered);
        setAccessRequestResultsCount(filtered.length);
        setLoading(false);
        if (!hasAutoOpened && filtered.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      }
    );
    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, firebase]);

  useEffect(() => {
    if (isOpen && results.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [results.length, isOpen, loading, setIsOpen]);

  const handleDismiss = async (result: AccessRequestResult) => {
    if (!currentOrgId || !firebase) return;
    setDismissingId(result.id);
    try {
      await firebase.dismissAccessRequestResult(currentOrgId, result.id);
    } catch (err) {
      console.error('Error dismissing access request result:', err);
    }
    setDismissingId(null);
  };

  const handleDismissAll = async () => {
    if (!currentOrgId || !firebase) return;
    for (const r of results) {
      try { await firebase.dismissAccessRequestResult(currentOrgId, r.id); } catch { /* best effort */ }
    }
  };

  const handleSkip = () => setIsOpen(false);

  if (!isOpen || results.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      contentClassName="delete-confirm-modal"
      style={{ color: '#ffffff', maxWidth: '700px' }}
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ color: '#fff' }}>
          Access Request Results ({results.length})
        </span>
      </ModalHeader>
      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '500px',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {results.map(r => {
            const isApproved = r.decision === 'approved';
            return (
              <div
                key={r.id}
                style={{
                  backgroundColor: isApproved ? 'rgba(80, 200, 120, 0.08)' : 'rgba(255, 80, 80, 0.08)',
                  border: `1px solid ${isApproved ? 'rgba(80, 200, 120, 0.3)' : 'rgba(255, 80, 80, 0.3)'}`,
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{
                        display: 'inline-block', padding: '1px 6px', fontSize: '0.65rem',
                        borderRadius: '3px', fontWeight: 600, letterSpacing: '0.3px',
                        backgroundColor: isApproved ? 'rgba(80, 200, 120, 0.25)' : 'rgba(255, 80, 80, 0.25)',
                        color: isApproved ? '#50c878' : '#ff5050',
                      }}>
                        {isApproved ? 'APPROVED' : 'DENIED'}
                      </span>
                      <span style={{ color: '#fff', fontWeight: 500 }}>{r.trackName}</span>
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                      {isApproved
                        ? <>The track is now available in your library.</>
                        : <>Your request was denied{r.sourceOrgName ? <> by <strong>{r.sourceOrgName}</strong></> : null}.</>
                      }
                    </div>
                    {!isApproved && r.denyReason && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: 'rgba(255, 80, 80, 0.1)',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        color: 'rgba(255, 255, 255, 0.75)',
                      }}>
                        Reason: {r.denyReason}
                      </div>
                    )}
                    <div style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      {moment(r.decidedAt).format('DD/MM/YYYY HH:mm')}
                    </div>
                  </div>
                  <Button
                    className="btn-info-custom"
                    size="sm"
                    disabled={dismissingId === r.id}
                    onClick={() => handleDismiss(r)}
                    style={{ fontSize: '0.75rem', padding: '4px 10px', flexShrink: 0 }}
                  >
                    <span style={{ position: 'relative', zIndex: 2 }}>
                      {dismissingId === r.id ? <><Spinner size="sm" className="mr-1" /> ...</> : 'Dismiss'}
                    </span>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '1rem 1.5rem',
          gap: '0.75rem',
        }}
      >
        {results.length > 1 && (
          <Button className="btn-info-custom" onClick={handleDismissAll}>
            <span style={{ position: 'relative', zIndex: 2 }}>Dismiss All</span>
          </Button>
        )}
        <Button className="btn-primary-custom" onClick={handleSkip}>
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AccessRequestResultsModal;
